import {
    type ContractVariant,
    widgetPunctualityCharttooltipDirectionContract,
} from 'aos-ds/src/patterns/widget-punctuality-charttooltip-direction/widget-punctuality-charttooltip-direction.contract'
import { Typography } from 'aos-ds/src/typography/Typography'
import { variantForStyleId } from 'aos-ds/src/typography/variants'
import React from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import { withoutSampleWidth } from '../../tailwind/withoutSampleWidth'
import {
    type PunctualityChartTooltipFlight,
    PunctualityChartTooltipFlightRow,
    type PunctualityChartTooltipUser,
} from '../punctuality-chart-tooltip-flight-row'

export const PUNCTUALITY_CHART_TOOLTIP_DIRECTIONS =
    widgetPunctualityCharttooltipDirectionContract.props.direction.values
export const PUNCTUALITY_CHART_TOOLTIP_DIRECTION_HOURS =
    widgetPunctualityCharttooltipDirectionContract.props.hour.values

export type PunctualityChartTooltipDirectionKey =
    (typeof PUNCTUALITY_CHART_TOOLTIP_DIRECTIONS)[number]
export type PunctualityChartTooltipDirectionHour =
    (typeof PUNCTUALITY_CHART_TOOLTIP_DIRECTION_HOURS)[number]

export type PunctualityChartTooltipSection = {
    text: string
    rows?: readonly PunctualityChartTooltipFlight[]
    more?: string
}

export type PunctualityChartTooltipDirectionData = {
    hour: PunctualityChartTooltipDirectionHour
    label: string
    value: string
    unit?: string
    sections: readonly PunctualityChartTooltipSection[]
}

export type PunctualityChartTooltipDirectionProps = PunctualityChartTooltipDirectionData & {
    direction: PunctualityChartTooltipDirectionKey
    user: PunctualityChartTooltipUser
    className?: string
    testID?: string
}

export const PUNCTUALITY_CHART_TOOLTIP_SECTION_PARTS: Record<
    PunctualityChartTooltipDirectionHour,
    readonly string[]
> = {
    'past': ['summary'],
    'current': ['part-departed', 'part-expected'],
    'forecast': ['summary'],
    'no prediction': ['reason'],
    'no flights': ['reason'],
}

const MORE = 'more'

const cells = widgetPunctualityCharttooltipDirectionContract.variants as Record<
    string,
    ContractVariant
>

export const punctualityChartTooltipDirectionKey = (
    direction: PunctualityChartTooltipDirectionKey,
    hour: PunctualityChartTooltipDirectionHour,
): string => `direction=${direction}, hour=${hour}`

export const resolvePunctualityChartTooltipDirection = (
    direction: PunctualityChartTooltipDirectionKey,
    hour: PunctualityChartTooltipDirectionHour,
): ContractVariant => {
    const key = punctualityChartTooltipDirectionKey(direction, hour)
    const cell = cells[key]
    if (!cell) {
        throw new Error(`PunctualityChartTooltipDirection: the Figma contract has no "${key}"`)
    }
    return cell
}

type TextPart = { className: string; variant: ReturnType<typeof variantForStyleId> }

const textPartOf = (cell: ContractVariant, name: string): TextPart | null => {
    const className = cell.parts[name]
    const style = cell.partTextStyles[name]
    return className === undefined || style === undefined
        ? null
        : { className, variant: variantForStyleId(style) }
}

// The dumper skips hidden layers, so a layer Figma only shows in an instance is read from another hour.
export const chartTooltipTextPart = (
    direction: PunctualityChartTooltipDirectionKey,
    hour: PunctualityChartTooltipDirectionHour,
    name: string,
): TextPart => {
    const own = textPartOf(resolvePunctualityChartTooltipDirection(direction, hour), name)
    const found =
        own ??
        PUNCTUALITY_CHART_TOOLTIP_DIRECTION_HOURS.map(other =>
            textPartOf(resolvePunctualityChartTooltipDirection(direction, other), name),
        ).find(part => part !== null)
    if (!found) {
        throw new Error(
            `PunctualityChartTooltipDirection: the Figma contract has no part "${name}"`,
        )
    }
    return found
}

const layoutPart = (cell: ContractVariant, name: string): string => {
    const found = cell.parts[name]
    if (found === undefined) {
        throw new Error(
            `PunctualityChartTooltipDirection: the Figma contract has no part "${name}"`,
        )
    }
    return found
}

/**
 * Figma `Widget/Punctuality/ChartTooltip/Direction` (19022:459741): one
 * direction of the hovered hour — the label, the direction's dot and value, and
 * the hour's lines and not-punctual flights. Which lines an hour has, the flights
 * and every text are the caller's; the cell picks the inks, the type and the
 * spacing. The frame width is the sample Figma drew; the direction fills the
 * tooltip.
 */
export const PunctualityChartTooltipDirection = ({
    direction,
    hour,
    user,
    label,
    value,
    unit,
    sections,
    className,
    testID = `punctuality-chart-tooltip-${direction}`,
}: PunctualityChartTooltipDirectionProps) => {
    const cell = resolvePunctualityChartTooltipDirection(direction, hour)
    const text = (name: string) => chartTooltipTextPart(direction, hour, name)
    const labelPart = text('label')
    const valuePart = text('value 2')
    const unitPart = textPartOf(cell, 'unit')
    const sectionParts = PUNCTUALITY_CHART_TOOLTIP_SECTION_PARTS[hour]

    return (
        <View
            className={twMerge('w-full', withoutSampleWidth(cell.container), className)}
            testID={testID}
        >
            <View className={twMerge('w-full flex-row', layoutPart(cell, 'header'))}>
                <Typography
                    variant={labelPart.variant}
                    className={twMerge('min-w-0 flex-1', cell.label, labelPart.className)}
                >
                    {label}
                </Typography>
                <View className={twMerge('flex-row', layoutPart(cell, 'value-group'))}>
                    <View className={cell.box} testID={`${testID}-dot`} />
                    <View className={twMerge('flex-row', layoutPart(cell, 'value'))}>
                        <Typography
                            variant={valuePart.variant}
                            className={valuePart.className}
                            testID={`${testID}-value`}
                        >
                            {value}
                        </Typography>
                        {unitPart && Boolean(unit) && (
                            <Typography variant={unitPart.variant} className={unitPart.className}>
                                {unit}
                            </Typography>
                        )}
                    </View>
                </View>
            </View>
            {sections.slice(0, sectionParts.length).map((section, index) => {
                const linePart = text(sectionParts[index])
                const morePart = section.more ? text(MORE) : null
                return (
                    <React.Fragment key={sectionParts[index]}>
                        <Typography
                            variant={linePart.variant}
                            className={twMerge('w-full', linePart.className)}
                            testID={`${testID}-line`}
                        >
                            {section.text}
                        </Typography>
                        {(section.rows ?? []).map((row, rowIndex) => (
                            <PunctualityChartTooltipFlightRow
                                key={`${row.flight}-${rowIndex}`}
                                user={user}
                                {...row}
                                testID={`${testID}-row`}
                            />
                        ))}
                        {morePart && (
                            <Typography
                                variant={morePart.variant}
                                className={twMerge('w-full', morePart.className)}
                                testID={`${testID}-more`}
                            >
                                {section.more}
                            </Typography>
                        )}
                    </React.Fragment>
                )
            })}
        </View>
    )
}

PunctualityChartTooltipDirection.displayName = 'PunctualityChartTooltipDirection'
